import { useContext } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { css } from '@emotion/css';
import type { ProjectClips } from '@trinity/chat/types';
import * as Table from '../Table';
import * as Layout from '../Table/Layout';
import { ControlledTablePagination } from '../presentation/TablePagination';
import { ProjectClipsPaginationContext } from '../../contexts/project-clips/Context';
import { ClipsTableEmpty } from './ClipsTableEmpty';
import { cx } from '@trinity/chat-components/utils';

type Props = {
  columns: ColumnDef<ProjectClips.Item>[];
  data: ProjectClips.Item[];
  empty: boolean;
  loading: boolean;
};

export const ClipsTable = ({ columns, data, empty, loading }: Props) => {
  const pagination = useContext(ProjectClipsPaginationContext);

  const table = useReactTable({
    data,
    getCoreRowModel: getCoreRowModel(),
    columns,
  });

  const paginationProps = {
    goToPage: pagination.setPageIndex,
    pageCount:
      pagination.totalRows % pagination.pageSize === 0
        ? pagination.totalRows / pagination.pageSize
        : Math.floor(pagination.totalRows / pagination.pageSize) + 1,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    totalCount: pagination.totalRows,
  };

  const getCellClass = (def: ColumnDef<ProjectClips.Item>): string | undefined =>
    def.meta?.['className'] as string | undefined;

  return (
    <Table.Root EmptyComponent={ClipsTableEmpty} empty={empty} loading={loading}>
      <Table.Header.THead>
        {table.getHeaderGroups().map((headerGroup) => (
          <Table.Header.TR key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <Table.Header.TH key={header.id} className={getCellClass(header.column.columnDef)}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </Table.Header.TH>
            ))}
          </Table.Header.TR>
        ))}
      </Table.Header.THead>
      <Table.Body.TBody>
        {table.getRowModel().rows.map((row) => (
          <Table.Body.TR key={row.id} className={trClass}>
            {row.getVisibleCells().map((cell) => (
              <Table.Body.TD
                key={cell.id}
                className={cx(tdClass, getCellClass(cell.column.columnDef))}
                rowIndex={cell.row.index}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Table.Body.TD>
            ))}
          </Table.Body.TR>
        ))}
      </Table.Body.TBody>
      <Layout.Footer className={footerClass}>
        <ControlledTablePagination {...paginationProps} />
      </Layout.Footer>
    </Table.Root>
  );
};

ClipsTable.displayName = 'ProjectClips.ClipsTable';

const trClass = css`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  padding: 10px 0;
`;

const tdClass = css`
  padding: 0 8px;
`;

const footerClass = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
